import * as Api from '@ViewModels';
import { StyleDeclarationValue, StyleSheet, css } from 'aphrodite';
import moment from 'moment';
import { ReactNode } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Waypoint } from 'react-waypoint';
import { useSelectionState } from '../../../hooks/useSelectionState';
import { useFullscreenModal, useUserSession } from '../../../models/hooks/appStateHooks';
import {
	isVolunteerBoardDisabledError,
	IVolunteer,
	IVolunteerFilter,
	useVolunteersQuery,
	VolunteerSortField,
} from '../../../queries/Volunteer';
import { Button } from '../../components-v2/Button';
import { Checkbox } from '../../components-v2/Checkbox';
import { THEME } from '../../components-v2/Theme/theme';
import { DataboardBody } from '../../components/Databoard';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '../../components/DataGrid';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { UsersActionBar } from '../../components/UsersActionBar';
import { ROUTES } from '../../constants/routes';
import { createContactClickHandler } from '../../containers/reporting/utils';
import { styleSheet } from './styles';

const volunteerName = (volunteer: IVolunteer) => {
	const fullName = `${volunteer.contact?.firstName ?? ''} ${volunteer.contact?.lastName ?? ''}`.trim();
	return volunteer.contact?.handle?.trim() || fullName || 'Unknown volunteer';
};

const volunteerEmail = (volunteer: IVolunteer) =>
	volunteer.contact?.primaryEmail?.value || volunteer.contact?.emailAddresses?.[0]?.value || '';

const formatDate = (iso?: string) => (iso ? moment(iso).format('MMM D, YYYY') : '—');

interface IProps {
	filter?: IVolunteerFilter;
	excludeActivityIds?: string[];
	sortBy: VolunteerSortField;
	emptyState: ReactNode;
	showActivityCount?: boolean;
	hasFooter?: boolean;
	bodyStyles?: StyleDeclarationValue[];
	onContactClick?: (contactId: string) => void;
	impersonationContext?: Api.IImpersonationContext;
}

export const VolunteersTable = ({
	filter,
	excludeActivityIds,
	sortBy,
	emptyState,
	showActivityCount = true,
	hasFooter,
	bodyStyles,
	onContactClick,
	impersonationContext,
}: IProps) => {
	const history = useHistory();
	const fullscreenModal = useFullscreenModal();
	const userSession = useUserSession();
	const { data, isLoading, isError, error, hasNextPage, fetchNextPage, isFetchingNextPage } = useVolunteersQuery({
		filter,
		excludeActivityIds,
		sortBy,
		impersonationContext,
	});
	const volunteers = data?.pages.flatMap(page => page.values) ?? [];

	const { isSelected, toggleRow, toggleSelectAll, isSelectionStateAll, isPartialSelected, selectionState, reset } =
		useSelectionState({ rows: volunteers.map(volunteer => volunteer.id) });

	const selectedContacts = (): Api.IProjectedContact[] =>
		volunteers
			.filter(volunteer => isSelected(volunteer.id) && !!volunteer.contact)
			.map(volunteer => volunteer.contact as Api.IProjectedContact);

	const onSendEmailClicked = () => {
		const contacts = selectedContacts();
		if (contacts.length === 0) {
			return;
		}
		fullscreenModal.history.push({
			pathname: '/email/campaigns/create/from-selection',
			state: {
				model: {
					context: {
						allowSenderSelection: true,
						contacts: contacts.map(contact => new Api.ContactViewModel(userSession, contact)),
					},
					type: 'Contacts',
				},
			},
		});
	};

	const renderContent = () => {
		if (isVolunteerBoardDisabledError(error)) {
			return <div className={css(styleSheet.emptyState)}>Volunteer management isn’t enabled for this account.</div>;
		}
		if (isLoading) {
			return (
				<div className={css(styleSheet.loadingState)}>
					<LoadingSpinner type='large' />
				</div>
			);
		}
		if (isError) {
			return <div className={css(styleSheet.emptyState)}>Couldn’t load volunteers.</div>;
		}
		if (volunteers.length === 0) {
			return emptyState;
		}

		return (
			<>
				<DataGrid styles={[styleSheet.tableOverrides]}>
					<DataGridColumns>
						<DataGridColumn styles={[tableStyles.checkboxColumn]}>
							<Checkbox
								id='select-all-volunteers'
								aria-label='Select all volunteers'
								checked={isSelectionStateAll ? true : isPartialSelected ? 'indeterminate' : false}
								onCheckedChange={toggleSelectAll}
							/>
						</DataGridColumn>
						<DataGridColumn>Volunteer</DataGridColumn>
						<DataGridColumn>Email</DataGridColumn>
						<DataGridColumn>Total hours</DataGridColumn>
						{showActivityCount ? <DataGridColumn>Activities</DataGridColumn> : null}
						<DataGridColumn>Participations</DataGridColumn>
						<DataGridColumn>Last activity</DataGridColumn>
					</DataGridColumns>
					<DataGridRows>
						{volunteers.map(volunteer => (
							<DataGridRow key={volunteer.id} styles={[styleSheet.tableRow]}>
								<DataGridRowItem>
									<Checkbox
										id={`select-volunteer-${volunteer.id}`}
										aria-label={`Select ${volunteerName(volunteer)}`}
										checked={isSelected(volunteer.id)}
										onClick={event => toggleRow(volunteer.id, event.shiftKey)}
									/>
								</DataGridRowItem>
								<DataGridRowItem>
									{volunteer.contactId && onContactClick ? (
										<Button
											variant='link'
											styles={[styleSheet.contactNameButton]}
											onClick={createContactClickHandler(volunteer.contactId, onContactClick, history, 'volunteers')}
										>
											{volunteerName(volunteer)}
										</Button>
									) : volunteer.contactId ? (
										<Link to={`${ROUTES.contacts.people}/${volunteer.contactId}`}>{volunteerName(volunteer)}</Link>
									) : (
										volunteerName(volunteer)
									)}
								</DataGridRowItem>
								<DataGridRowItem styles={[styleSheet.mutedCell]}>{volunteerEmail(volunteer) || '—'}</DataGridRowItem>
								<DataGridRowItem>{(volunteer.totalHours ?? 0).toFixed(1)}</DataGridRowItem>
								{showActivityCount ? <DataGridRowItem>{volunteer.activityCount ?? 0}</DataGridRowItem> : null}
								<DataGridRowItem>{volunteer.participationCount ?? 0}</DataGridRowItem>
								<DataGridRowItem styles={[styleSheet.mutedCell]}>
									{formatDate(volunteer.lastActivityDate)}
								</DataGridRowItem>
							</DataGridRow>
						))}
					</DataGridRows>
				</DataGrid>

				{!isFetchingNextPage && hasNextPage ? <Waypoint bottomOffset='-200px' onEnter={() => fetchNextPage()} /> : null}
				{isFetchingNextPage ? (
					<div className={css(styleSheet.loadMore)}>
						<LoadingSpinner type='small' />
					</div>
				) : null}
			</>
		);
	};

	return (
		<>
			{volunteers.length > 0 ? (
				<div className={css(tableStyles.toolbar)}>
					<UsersActionBar
						selectionState={selectionState}
						getMatchingContacts={async () => selectedContacts()}
						onSendEmailClicked={onSendEmailClicked}
						onDeselectAll={reset}
					/>
				</div>
			) : null}
			<DataboardBody hasFooter={hasFooter} styles={bodyStyles}>
				{renderContent()}
			</DataboardBody>
		</>
	);
};

const tableStyles = StyleSheet.create({
	toolbar: { display: 'flex', alignItems: 'center', marginBottom: THEME.spacing.md },
	checkboxColumn: { width: 40 },
});
